import { intl } from "LocaleWrapper";

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import GridGoldenratioIcon from "@mui/icons-material/GridGoldenratio";
import DoorSlidingIcon from "@mui/icons-material/DoorSliding";
import StraightenIcon from "@mui/icons-material/Straighten";
import NotificationsIcon from "@mui/icons-material/Notifications";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";

import { ChildProps, ChildPropsFromArray } from "types/commons";
import { GetVoyagePlanQuery } from "__generated__/view/voyages/graphql/voyage.queries.generated";
import { CargoForVoyagePlanFragment } from "__generated__/view/cargos/graphql/cargo.fragments.generated";
import { CallType, VolumeUnit } from "__generated__/enums";

export enum AnglerEvents {
	Waiting = "waiting",
	Bridge = "bridge",
	Lock = "lock",
	MeasuringPoint = "measuringPoint",
	Alert = "alert",
	Warning = "warning",
	Unknown = "unknown"
}

export type AnglerTagSettings = {
	[key in AnglerEvents]: {
		visible: boolean;
		color: string;
		label: string;
		icon: React.ReactElement;
	};
}

export type VoyagePlan = ChildProps<GetVoyagePlanQuery, "voyagePlan">;
export type VoyagePlanEvent = ChildPropsFromArray<VoyagePlan, "events">;
export type VoyagePlanCallEvent = Extract<VoyagePlanEvent, { __typename: "CallEvent" }>;
export type VoyagePlanWaitingEvent = Extract<VoyagePlanEvent, { __typename: "WaitingEvent" }>;
export type VoyagePlanBridgeEvent = Extract<VoyagePlanEvent, { __typename: "BridgeEvent" }>;
export type VoyagePlanLockEvent = Extract<VoyagePlanEvent, { __typename: "LockEvent" }>;
export type VoyagePlanMeasuringPointEvent = Extract<VoyagePlanEvent, { __typename: "MeasuringPointEvent" }>;

export type ProductInformation = {
	product: {
		id: number;
		name: string;
	}
	weight: number;
	volume: number,
	locationVolumeL15: number,
	locationWeightMTon: number,
	skipperVolumeL15: number,
	skipperWeightMTon: number,
}

export type AnglerRoute = {
	nextCall: VoyagePlanCallEvent | undefined;
	distanceKm: number;
	durationMin: number;
	nextSluice: VoyagePlanLockEvent | undefined;
	lowestWaterLevel: VoyagePlanMeasuringPointEvent | undefined;
}

export const getInitialTagSettings = (): AnglerTagSettings => ({
	[AnglerEvents.Waiting]: {
		visible: false,
		color: "#3190D8",
		label: intl.formatMessage({ id: "ANGLER_WAITING_TIME" }),
		icon: <AccessTimeIcon fontSize="small" />,
	},
	[AnglerEvents.Bridge]: {
		visible: false,
		color: "#3ED6BB",
		label: intl.formatMessage({ id: "ANGLER_BRIDGES" }),
		icon: <GridGoldenratioIcon fontSize="small" />,
	},
	[AnglerEvents.Lock]: {
		visible: false,
		color: "#A84CD0",
		label: intl.formatMessage({ id: "ANGLER_LOCKS" }),
		icon: <DoorSlidingIcon fontSize="small" />,
	},
	[AnglerEvents.MeasuringPoint]: {
		visible: false,
		color: "#0f5e9c",
		label: intl.formatMessage({ id: "WATER_LEVELS" }),
		icon: <StraightenIcon fontSize="small" sx={{ transform: "rotate(90deg)" }} />,
	},
	[AnglerEvents.Alert]: {
		visible: false,
		color: "#D23838",
		label: intl.formatMessage({ id: "ANGLER_ALERT" }),
		icon: <NotificationsIcon fontSize="small" />,
	},
	[AnglerEvents.Warning]: {
		visible: false,
		color: "#D18427",
		label: intl.formatMessage({ id: "ANGLER_WARNING" }),
		icon: <WarningAmberIcon fontSize="small" />,
	},
	[AnglerEvents.Unknown]: {
		visible: false,
		color: "#3190D8",

		label: intl.formatMessage({ id: "UNKNOWN" }),
		icon: <QuestionMarkIcon fontSize="small" />,
	},
});

export function getCallProductsInfo(
	call: NonNullable<VoyagePlanCallEvent["referable"]>,
	cargos: CargoForVoyagePlanFragment[],
	type: CallType.Loading | CallType.Discharging,
): ProductInformation[] {
	const productsMap = new Map();

	cargos.forEach(cargo => {
		cargo.actions.forEach(action => {
			const productId = cargo.product?.id;
			const isCorrectCall = action.call?.id === call.id && action.type === type;

			if (isCorrectCall) {
				if (!productsMap.has(productId)) {
					productsMap.set(productId, {
						product: cargo.product,
						weight: 0,
						volume: 0,
						locationVolumeL15: 0,
						locationWeightMTon: 0,
						skipperVolumeL15: 0,
						skipperWeightMTon: 0,
					});
				}

				const entry = productsMap.get(productId);
				entry.weight += Number(action.weight ?? 0);
				entry.volume += Number(action.volume ?? 0);
				entry.locationVolumeL15 += action.measurements.locationVolume ?? 0;
				entry.locationWeightMTon += action.measurements.locationWeight ?? 0;
				entry.skipperVolumeL15 += action.measurements.skipperVolume ?? 0;
				entry.skipperWeightMTon += action.measurements.skipperWeight ?? 0;
			}
		});
	});

	return Array.from(productsMap.values()).map(response => {
		return {
			...response,
			locationVolumeL15: response.locationVolumeL15 / 100000,
			locationWeightMTon: response.locationWeightMTon / 100000,
			skipperVolumeL15: response.skipperVolumeL15 / 100000,
			skipperWeightMTon: response.skipperWeightMTon / 100000,
		};
	});
}

export function getRouteBetweenCalls(currentCallEvent: VoyagePlanCallEvent, events: VoyagePlanEvent[]) {

	let nextCall: VoyagePlanCallEvent | undefined = undefined;
	let distanceKm: number = 0;
	let durationMin: number = 0;
	let nextSluice: VoyagePlanLockEvent | undefined = undefined;
	let lowestWaterLevel: VoyagePlanMeasuringPointEvent | undefined = undefined;

	const startIndex = events.findIndex(event => event.__typename === "CallEvent" && event.referable?.id === currentCallEvent.referable?.id);

	if (startIndex !== -1) {
		const endIndex = events.findIndex((event, index) => index > startIndex && event.__typename === "CallEvent");

		if (endIndex !== -1) {
			nextCall = events[endIndex] as VoyagePlanCallEvent;
		}

		const sliceEnd = endIndex !== -1 ? endIndex : events.length;
		const eventsTillNextCall = events.slice(startIndex, sliceEnd);

		for (const event of eventsTillNextCall) {
			if (!event) {
				continue;
			}

			if (event.__typename === "ShippingEvent") {
				distanceKm += event.distanceKm ?? 0;
				durationMin += event.durationMin ?? 0;
			}

			if (event.__typename === "LockEvent" && !nextSluice) {
				nextSluice = event;
			}

			if (
				event.__typename === "MeasuringPointEvent" &&
				event.measurement?.waterLevel !== undefined &&
				event.measurement?.waterLevel !== null
			) {

				const currentWaterLevel: number = event.measurement.waterLevel;
				const lowestRecordedLevel = lowestWaterLevel?.measurement?.waterLevel;

				if (
					lowestRecordedLevel === undefined ||
					lowestRecordedLevel === null ||
					currentWaterLevel < lowestRecordedLevel
				) {
					lowestWaterLevel = event;
				}
			}
		}
	}

	return {
		distanceKm: distanceKm,
		durationMin: durationMin,
		nextCall: nextCall,
		nextSluice: nextSluice,
		lowestWaterLevel: lowestWaterLevel,
	};
}
